import {
  type AfterContentInit,
  afterNextRender,
  booleanAttribute,
  Component,
  computed,
  contentChild,
  contentChildren,
  ElementRef,
  inject,
  Injector,
  input,
  type InputSignal,
  type InputSignalWithTransform,
  model,
  type ModelSignal,
  type OnDestroy,
  type OnInit,
  output,
  type OutputEmitterRef,
  signal,
  type Signal,
  viewChild,
  type WritableSignal
} from '@angular/core';
import { ActiveDescendantKeyManager, FocusKeyManager } from '@angular/cdk/a11y';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { outputToObservable, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, merge, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { TranslateDirective } from '@ngx-translate/core';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { ControlValueAccessor } from '@angular/forms';

import { KeyCode } from '../../domain/key-code.enum';
import { IdService } from '../../services/id.service';
import { PlatformService } from '../../services/platform.service';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { IconComponent } from '../icon/icon.component';
import { SideSheetSwipeDirective } from '../../directives/side-sheet/side-sheet-swipe.directive';
import { BottomModalComponent } from '../bottom-modal/bottom-modal.component';
import { SideSheetSelectorToggleDirective } from './side-sheet-selector-toggle.directive';
import { SideSheetSelectorItemComponent } from './side-sheet-selector-item.component';
import { SideSheetSelectorToggleStaticDirective } from './side-sheet-selector-toggle-static.directive';
import type { VerticalConnectionPos } from '@angular/cdk/overlay';
import type { SelectionChange } from '@angular/cdk/collections';

import type { ChangeEventOn, SelectableItem, SelectionChangeEvent } from '../../domain/selectable.domain';

export type SideSheetType = 'primary' | 'secondary';

export enum SelectorToggleType {
  NONE = 'none',
  ARROW = 'arrow'
}

const DEFAULT_COMPARATOR = <T>(obj1: T, obj2: T): boolean => obj1 === obj2;

@Component({
  selector: 'td-wb-side-sheet-selector',
  templateUrl: 'side-sheet-selector.component.html',
  styleUrls: ['side-sheet-selector.component.scss'],
  providers: [ValueAccessorProviderFactory(SideSheetSelectorComponent)],
  exportAs: 'tdWbSideSheetSelector',
  imports: [
    CdkOverlayOrigin,
    BottomModalComponent,
    SideSheetSwipeDirective,
    TranslateDirective,
    IconComponent
  ],
  host: {
    '[class.td-wb-side-sheet-selector--secondary]': 'sheetType() === "secondary"',
    '[attr.disabled]': 'isDisabled() ? "" : null',
    '[attr.aria-disabled]': 'isDisabled()',
    '[attr.aria-label]': 'ariaLabelAttr()',
    '[attr.aria-labelledby]': 'ariaLabelledBy()',
    '[attr.aria-activedescendant]': 'ariaActiveDescendantAttr()',
    '[attr.aria-expanded]': 'panelDisplayed()',
    '[attr.aria-controls]': 'ariaControls()',
    '[attr.tabindex]': 'tabIndex()',
    '[attr.role]': '\'combobox\'',
    '[attr.aria-haspopup]': '\'listbox\'',
    '(click)': 'onClick()',
    '(keydown)': 'onKeyDown($event)'
  }
})
export class SideSheetSelectorComponent<T> implements ControlValueAccessor, OnDestroy, OnInit, AfterContentInit {
  private static readonly LABEL_ID_PREFIX: string = 'td-wb-side-sheet-selector-label';

  readonly placeholder: InputSignal<string | undefined> = input<string | undefined>();
  readonly userDefinedPanelMaxHeight: InputSignal<number | undefined> = input<number | undefined>(undefined, { alias: 'panelMaxHeight' });
  readonly toggleType: InputSignal<SelectorToggleType> = input<SelectorToggleType>(SelectorToggleType.ARROW);
  readonly comparator: InputSignal<(obj1: T, obj2: T) => boolean> = input<(obj1: T, obj2: T) => boolean>(DEFAULT_COMPARATOR);
  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'ariaLabel' });
  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-labelledby' });
  readonly id: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'id' });
  readonly sheetType: InputSignal<SideSheetType> = input<SideSheetType>('primary');
  readonly disabled: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, { transform: booleanAttribute });
  private readonly disabledByCva = signal(false);
  readonly isDisabled: Signal<boolean> = computed(() => this.disabled() || this.disabledByCva());

  readonly onDropdownClick: OutputEmitterRef<void> = output<void>();

  readonly panel: Signal<BottomModalComponent | undefined> = viewChild('panel', { read: BottomModalComponent });
  readonly items: Signal<ReadonlyArray<SideSheetSelectorItemComponent<T>>> = contentChildren(SideSheetSelectorItemComponent, { descendants: true });
  readonly customToggle: Signal<SideSheetSelectorToggleDirective | undefined> = contentChild(SideSheetSelectorToggleDirective);
  readonly staticToggle: Signal<SideSheetSelectorToggleStaticDirective | undefined> = contentChild(SideSheetSelectorToggleStaticDirective);

  readonly tabIndex: Signal<string> = computed(() => this.isDisabled() ? '-1' : '0');

  readonly ariaLabelAttr: Signal<string | null> = computed(() => this.ariaLabel() ?? this.placeholder() ?? null);

  readonly ariaActiveDescendantAttr: Signal<string | null> = computed(() => {
    if (this.panelDisplayed() && this.keyManager !== undefined && this.keyManager.activeItem !== null) {
      return this.keyManager.activeItem.id;
    }
    return null;
  });

  readonly ariaControls: Signal<string> = computed(() => this.panelDisplayed() ? 'td-wb-side-sheet-selector__panel-' + this.labelId() : '');

  panelPosition: WritableSignal<VerticalConnectionPos> = signal<VerticalConnectionPos>('bottom');
  generateLabel: WritableSignal<boolean> = signal(false);
  labelId: WritableSignal<string | undefined> = signal(undefined);
  panelMaxHeight: WritableSignal<number> = signal(0);
  platformIOS: WritableSignal<boolean> = signal(false);
  isPanelDisplayed: Subject<boolean> = new Subject<boolean>();
  panelDisplayed: WritableSignal<boolean> = signal(false);

  private disableToggle: boolean = false;
  private readonly destroy: Subject<void> = new Subject<void>();
  private readonly injector = inject(Injector);
  /**
   * Single source of truth, and the Signal Forms native custom-control binding.
   * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
   */
  readonly value: ModelSignal<T | null | undefined> = model<T | null | undefined>(undefined);

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();
  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};
  private viewPortRect!: {
    top: number;
    left: number;
    bottom: number;
    right: number;
    height: number;
    width: number;
  };

  private readonly elementRef = inject(ElementRef);
  private readonly viewportRuler = inject(ViewportRuler);
  private readonly platformService = inject(PlatformService);
  private readonly idService = inject(IdService);
  private keyManager!: ActiveDescendantKeyManager<SelectableItem<T>> | FocusKeyManager<SelectableItem<T>>;
  private readonly selectionModel: SelectionModel<SelectableItem<T>> = new SelectionModel<SelectableItem<T>>(false);
  private readonly selectedItems: WritableSignal<ReadonlyArray<SelectableItem<T>>> = signal<ReadonlyArray<SelectableItem<T>>>([]);
  private keyManagerInitialized: boolean = false;

  private readonly items$ = toObservable(this.items, { injector: this.injector });

  private readonly optionSelectionChanges: ChangeEventOn<SelectableItem<T>> = toObservable(this.items, { injector: this.injector }).pipe(
    switchMap((items: ReadonlyArray<SideSheetSelectorItemComponent<T>>) => {
      if (items.length > 0) {
        return merge(...items.map((item: SideSheetSelectorItemComponent<T>) => outputToObservable(item.onSelectionChange)));
      }
      return EMPTY;
    })
  );

  constructor() {
    this.items$
      .pipe(takeUntil(this.destroy))
      .subscribe((items: ReadonlyArray<SideSheetSelectorItemComponent<T>>) => {
        if (items.length === 0) {
          return;
        }

        items.forEach((item: SideSheetSelectorItemComponent<T>, index: number) => {
          item.first.set(index === 0);
          item.last.set(index === items.length - 1);
        });

        this.setSelectionByValue(this.value());

        // Re-initialize key manager when items change (e.g., dynamically loaded items)
        if (this.keyManagerInitialized) {
          this.reinitializeKeyManager();
        }
      });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.isDisabled()) {
      if (this.panelDisplayed()) {
        this.onKeyDownOpen(event);
      } else {
        this.onKeyDownClose(event);
      }
    }
  }

  onClick(): void {
    this.toggle();
  }

  ngOnInit(): void {
    this.platformIOS.set(this.platformService.isIOS());
  }

  ngOnDestroy(): void {
    this.close();
    this.destroy.next();
    this.destroy.complete();
  }

  ngAfterContentInit(): void {
    this.initKeyManager();

    this.selectionModel.changed
      .pipe(takeUntil(this.destroy))
      .subscribe((event: SelectionChange<SelectableItem<T>>): void => {
        event.added.forEach((item: SelectableItem<T>): void => item.select(false));
        event.removed.forEach((item: SelectableItem<T>): void => item.deselect(false));
        this.selectedItems.set(this.selectionModel.selected);
      });

    this.optionSelectionChanges.pipe(takeUntil(this.destroy)).subscribe((event: SelectionChangeEvent<SelectableItem<T>>): void => {
      this.onSelect(event.source, event.fromView);
      if (event.fromView && this.isOpen()) {
        this.close();
        this.focus();
      }
    });

    this.initAriaAttributes();
  }

  initAriaAttributes(): void {
    const ariaLabel = this.ariaLabel() ?? this.placeholder();
    if (ariaLabel !== undefined && ariaLabel !== null) {
      this.labelId.set(this.idService.generateNamespaceSequentialId(SideSheetSelectorComponent.LABEL_ID_PREFIX));
      this.generateLabel.set(true);
    }
  }

  toggle(): void {
    this.isOpen() ? this.close() : this.open();
  }

  close(): void {
    if (this.panelDisplayed()) {
      this.disableToggle = true;
      // needed because the click host handler triggers on close to open it inadvertently. The alternative, (click) event listener on the
      // toggle, does not work in TalkBack, a known angular bug: https://github.com/angular/components/issues/18005
      setTimeout((): void => {
        this.disableToggle = false;
      });
      this.panelDisplayed.set(false);
      this.keyManager.withHorizontalOrientation('ltr');
    }
  }

  isOpen(): boolean {
    return this.panelDisplayed();
  }

  isEmpty(): boolean {
    return this.selectedItems().length === 0;
  }

  open(): void {
    const items = this.items();
    if (this.isDisabled() || items.length === 0 || this.panelDisplayed() || this.disableToggle) {
      return;
    }

    this.viewPortRect = this.viewportRuler.getViewportRect();
    const userMaxHeight = this.userDefinedPanelMaxHeight();
    const maxHeight = userMaxHeight !== undefined && userMaxHeight < this.viewPortRect.height * 0.4 ?
      userMaxHeight : this.viewPortRect.height * 0.4;
    this.panelMaxHeight.set(maxHeight);
    this.panelDisplayed.set(true);
    this.keyManager.withHorizontalOrientation(null);
    this.highlightItem();

    afterNextRender(() => {
      if (this.panel()) {
        if (this.platformIOS() && items.length > 0) {
          items[0].focus();
        }
      }
    }, { injector: this.injector });
    this.onDropdownClick.emit();
  }

  isToggleHidden(): boolean {
    return this.toggleType() === SelectorToggleType.NONE;
  }

  focus(): void {
    this.elementRef.nativeElement.focus();
  }

  writeValue(value: T | null | undefined): void {
    this.value.set(value);
    if (this.items().length > 0) {
      this.setSelectionByValue(value);
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByCva.set(isDisabled);
  }

  getSelectedItem(): SelectableItem<T> {
    return this.selectedItems()[0];
  }

  getLabelledbyValue(): string | null {
    const id = this.id();
    let label: string;
    if (this.ariaLabelledBy() !== undefined) {
      label = this.ariaLabelledBy()!;
    } else if (this.generateLabel()) {
      label = this.labelId()!;
    } else {
      label = '';
    }
    const hasId = id !== undefined && id !== null;

    let labelledBy: string | undefined;
    if (hasId && this.isOpen()) {
      labelledBy = `${label}`;
    } else if (hasId) {
      labelledBy = `${label} ${id}`;
    } else {
      labelledBy = undefined;
    }
    return labelledBy ?? null;
  }

  onToggle(value: boolean): void {
    this.isPanelDisplayed.next(value);
  }

  private onKeyDownOpen(event: KeyboardEvent): void {
    const keyCode = event.key;
    const isArrowKey = keyCode === KeyCode.ARROW_DOWN || keyCode === KeyCode.ARROW_UP;
    const keyManager = this.keyManager;
    let preventDefault = true;

    if (keyCode === KeyCode.HOME) {
      keyManager.setFirstItemActive();
    } else if (keyCode === KeyCode.END) {
      keyManager.setLastItemActive();
    } else if (isArrowKey && event.altKey) {
      this.close();
    } else if ((keyCode === KeyCode.ENTER || keyCode === KeyCode.SPACE) && keyManager.activeItem !== null) {
      keyManager.activeItem.select(true);
    } else {
      keyManager.onKeydown(event);
      preventDefault = false;
    }
    if (preventDefault) {
      event.preventDefault();
    }
  }

  private onKeyDownClose(event: KeyboardEvent): void {
    const keyCode = event.key;
    const isArrowKey = keyCode === KeyCode.ARROW_DOWN || keyCode === KeyCode.ARROW_UP ||
      keyCode === KeyCode.ARROW_LEFT || keyCode === KeyCode.ARROW_RIGHT;
    const isOpenKey = keyCode === KeyCode.ENTER || keyCode === KeyCode.SPACE;

    if (isOpenKey || isArrowKey) {
      event.preventDefault();
      this.open();
    } else {
      this.keyManager.onKeydown(event);
    }
  }

  private highlightItem(): void {
    if (this.isEmpty()) {
      this.keyManager.setFirstItemActive();
    } else {
      this.keyManager.setActiveItem(this.selectionModel.selected[0]);
    }
  }

  private initKeyManager(): void {
    this.createKeyManager();

    this.keyManager.tabOut.pipe(takeUntil(this.destroy)).subscribe((): void => {
      this.focus();
      this.close();
    });

    this.keyManager.change.pipe(takeUntil(this.destroy)).subscribe((): void => {
      if (!this.panelDisplayed() && this.keyManager.activeItem !== null &&
        this.keyManager.activeItem !== undefined) {
        this.keyManager.activeItem.select(true);
      }
    });

    this.keyManagerInitialized = true;
  }

  private reinitializeKeyManager(): void {
    // Recreate the key manager with updated items when they change dynamically
    // (e.g., asynchronously loaded items). No need to re-subscribe to
    // tabOut and change events as they are already scoped to takeUntil(this.destroy).
    this.createKeyManager();
  }

  private createKeyManager(): void {
    if (this.platformIOS()) {
      this.keyManager = new FocusKeyManager<SideSheetSelectorItemComponent<T>>(this.items())
        .withTypeAhead()
        .withVerticalOrientation()
        .withHorizontalOrientation('ltr');
    } else {
      this.keyManager = new ActiveDescendantKeyManager<SideSheetSelectorItemComponent<T>>(this.items())
        .withTypeAhead()
        .withVerticalOrientation()
        .withHorizontalOrientation('ltr');
    }
  }

  private onSelect(item: SelectableItem<T>, isUserInput: boolean): void {
    const wasSelected = this.selectionModel.isSelected(item);

    if (item.getValue() === null || item.getValue() === undefined) {
      item.deselect();
      this.selectionModel.clear();
      this.propagateChanges(item.getValue(), isUserInput);
    } else {
      if (item.isSelected()) {
        this.selectionModel.select(item);
      } else {
        this.selectionModel.deselect(item);
      }
      if (isUserInput) {
        this.keyManager.setActiveItem(item);
      }
    }
    if (wasSelected !== this.selectionModel.isSelected(item)) {
      this.propagateChanges(undefined, isUserInput);
    }
    if (isUserInput) {
      this.onTouchedCallback();
      this.touch.emit();
    }
  }

  private propagateChanges(value: T | null | undefined = undefined, isUserInput: boolean = false): void {
    const selected = this.getSelectedItem();
    const updatedValue = selected === undefined ? value : selected.getValue();
    this.value.set(updatedValue);
    if (isUserInput) {
      this.onChangeCallback(updatedValue);
    }
  }

  private setSelectionByValue(value: T | null | undefined): void {
    this.selectionModel.clear();
    const item: SelectableItem<T> | undefined = this.selectValue(value);
    if (item !== undefined && this.keyManager !== undefined) {
      this.keyManager.setActiveItem(item);
    }
  }

  private selectValue(value: T | null | undefined): SideSheetSelectorItemComponent<T> | undefined {
    const selectorItemComponent = this.items()
      .find((item: SideSheetSelectorItemComponent<T>): boolean => {
        const itemValue = item.value();
        return itemValue != null && this.comparator()(itemValue, value);
      });

    if (selectorItemComponent !== undefined) {
      this.selectionModel.select(selectorItemComponent);
    }

    return selectorItemComponent;
  }

}
